// ForceSend.js - Anlık İçerik Gönder Sayfası
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  FiSend,
  FiChevronDown,
  FiChevronRight,
  FiCheck,
  FiBook,
  FiFileText,
  FiImage,
  FiLink,
  FiMessageSquare,
} from "react-icons/fi";
import SharedLayout from "./SharedLayout";
import InfoModal from "./modals/InfoModals";
import apiClient from "./services/apiClient";
import "./App.css";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL; // Removed

const ForceSend = () => {
  const { userId } = useParams();

  // Courses state
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  // Selection state
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedLessonIds, setSelectedLessonIds] = useState([]);

  // Option states
  const [resendEnabled, setResendEnabled] = useState(false);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [sendReport, setSendReport] = useState(null);

  // Info modal state
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  const showInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // Fetch admin courses
  const fetchCourses = useCallback(async () => {
    try {
      setLoadingCourses(true);
      // v2: tenant-based admin courses endpoint
      const data = await apiClient.get("/courses/admin");

      // Handle both plain array and { courses: [...] } shapes
      const allCourses = Array.isArray(data) ? data : data?.courses || [];

      // Only show PUBLISHED courses
      const publishedCourses = allCourses.filter(
        (course) => course.status === "PUBLISHED",
      );
      setCourses(publishedCourses);
    } catch (err) {
      console.error("Error fetching courses:", err);
      showInfo(
        "error",
        "Kurslar yüklenemedi",
        err.formatted?.message || "Kurs listesi alınırken bir hata oluştu.",
      );
    } finally {
      setLoadingCourses(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Toggle course expansion
  const toggleCourseExpansion = (courseId) => {
    if (expandedCourseId === courseId) {
      setExpandedCourseId(null);
    } else {
      setExpandedCourseId(courseId);
    }
  };

  // Select/deselect course
  const handleCourseSelect = (course) => {
    setSendReport(null);

    if (selectedCourseId === course.id) {
      setSelectedCourseId(null);
      setSelectedLessonIds([]);
      setExpandedCourseId(null);
    } else {
      setSelectedCourseId(course.id);
      setSelectedLessonIds([]);
      setExpandedCourseId(course.id);
    }
  };

  // Toggle lesson selection
  const toggleLessonSelection = (lessonId) => {
    setSelectedLessonIds((prev) => {
      if (prev.includes(lessonId)) {
        return prev.filter((id) => id !== lessonId);
      } else {
        return [...prev, lessonId];
      }
    });
  };

  // Get selected course
  const getSelectedCourse = () => {
    return courses.find((c) => c.id === selectedCourseId);
  };

  const getOrderedLessons = (course) =>
    [...(course?.lessons || [])].sort((a, b) => a.day - b.day);

  const lessonHasQuiz = (lesson) => {
    if (Array.isArray(lesson?.quiz?.questions)) {
      return lesson.quiz.questions.length > 0;
    }
    return Boolean(lesson?.quiz?.question);
  };

  const lessonHasQuestionFeedback = (lesson) => {
    if (Array.isArray(lesson?.quiz?.questions)) {
      return lesson.quiz.questions.some(
        (q) =>
          Boolean((q?.correctFeedback || "").trim()) ||
          Boolean((q?.wrongFeedback || "").trim()),
      );
    }

    return (
      Boolean((lesson?.quiz?.correctFeedback || "").trim()) ||
      Boolean((lesson?.quiz?.wrongFeedback || "").trim())
    );
  };

  const getLessonBadges = (lesson) => {
    const badges = [];

    if (lesson.media) {
      badges.push({
        label: "MEDYA",
        className: "bg-purple-50 text-purple-700",
      });
    }

    if (lesson.document) {
      badges.push({
        label: "DOKÜMAN",
        className: "bg-slate-100 text-slate-700",
      });
    }

    if (lesson.externalLink) {
      badges.push({
        label: "LİNK",
        className: "bg-emerald-50 text-emerald-700",
      });
    }

    if (lessonHasQuiz(lesson)) {
      badges.push({
        label: "SORU",
        className: "bg-blue-50 text-blue-700",
      });
    }

    if (lessonHasQuestionFeedback(lesson)) {
      badges.push({
        label: "SORU GERİ BİLDİRİMİ",
        className: "bg-indigo-50 text-indigo-700",
      });
    }

    if ((lesson.completionFeedback || "").trim()) {
      badges.push({
        label: "TAMAMLAMA GERİ BİLDİRİMİ",
        className: "bg-green-50 text-green-700",
      });
    }

    if ((lesson.completionReminderMessage || "").trim()) {
      badges.push({
        label: "TAMAMLAMA HATIRLATMASI",
        className: "bg-orange-50 text-orange-700",
      });
    }

    return badges;
  };

  const selectAllLessons = () => {
    const course = getSelectedCourse();
    setSelectedLessonIds(getOrderedLessons(course).map((lesson) => lesson.id));
  };

  const clearLessonSelection = () => {
    setSelectedLessonIds([]);
  };

  const selectOnlyQuizLessons = () => {
    const course = getSelectedCourse();
    setSelectedLessonIds(
      getOrderedLessons(course)
        .filter((lesson) => lessonHasQuiz(lesson))
        .map((lesson) => lesson.id),
    );
  };

  const selectOnlyRichContentLessons = () => {
    const course = getSelectedCourse();
    setSelectedLessonIds(
      getOrderedLessons(course)
        .filter(
          (lesson) => lesson.media || lesson.document || lesson.externalLink,
        )
        .map((lesson) => lesson.id),
    );
  };

  // Get send info text
  const getSendInfoText = () => {
    const course = getSelectedCourse();
    if (!course) return "";

    const lessonCount = course.lessons?.length || 0;
    const selectedCount = selectedLessonIds.length;

    if (selectedCount === 0) {
      return `Tüm ${lessonCount} içerik gönderilecek`;
    } else {
      return `${selectedCount} içerik gönderilecek`;
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedCourseId) {
      showInfo("warning", "Kurs seçimi gerekli", "Lütfen bir kurs seçiniz!");
      return;
    }

    const course = getSelectedCourse();
    if (!course) return;

    setIsSubmitting(true);

    const requestBody = {
      adminId: parseInt(userId),
      resend: resendEnabled,
    };

    // Only add lessonIds if specific lessons are selected
    if (selectedLessonIds.length > 0) {
      requestBody.lessonIds = selectedLessonIds;
    }

    try {
      // apiClient unwrap
      const data = await apiClient.post(
        `/courses/${selectedCourseId}/force-send`,
        requestBody,
      );

      if (data.success) {
        const sentCount = data.sentCount || data.jobsQueued || 0;
        const lessonCount =
          data.lessonCount ||
          selectedLessonIds.length ||
          course.lessons?.length ||
          0;

        setSuccessMessage(
          `✅ ${lessonCount} içerik ${sentCount} kişiye başarıyla gönderildi!`,
        );

        setSendReport({
          courseName: course.name,
          lessonCount,
          recipientCount: data.recipientCount || 0,
          jobsQueued: data.jobsQueued || 0,
          sentCount,
          skippedCount: Array.isArray(data.skipped) ? data.skipped.length : 0,
          resend: Boolean(data.resend),
        });

        setSelectedCourseId(null);
        setSelectedLessonIds([]);
        setExpandedCourseId(null);
        setResendEnabled(false);

        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        showInfo("error", "Hata", data.error || "Bir hata oluştu.");
      }
    } catch (error) {
      console.error("Force send error:", error);
      const errorMessage =
        error.formatted?.message ||
        error.message ||
        "İçerik gönderilirken bir hata oluştu.";
      showInfo("error", "Hata", errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedLayout pageTitle="ANLIK İÇERİK GÖNDER">
      <InfoModal
        isOpen={info.open}
        onClose={closeInfo}
        type={info.type}
        title={info.title}
        message={info.message}
      />

      <div className="max-w-4xl mx-auto">
        {/* Success Banner */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-2xl mb-4 flex items-center justify-between shadow-sm">
            <span className="font-semibold text-lg">{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-700 hover:text-green-900 font-bold text-xl"
            >
              ×
            </button>
          </div>
        )}

        {sendReport && (
          <div className="bg-white border border-green-200 rounded-2xl p-5 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Anlık İçerik Gönderim Raporu
              </h3>
              <button
                type="button"
                onClick={() => setSendReport(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Kapat
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Kurs</div>
                <div className="font-semibold text-gray-800">
                  {sendReport.courseName}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">İçerik Sayısı</div>
                <div className="font-semibold text-gray-800">
                  {sendReport.lessonCount}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Hedef Kişi</div>
                <div className="font-semibold text-gray-800">
                  {sendReport.recipientCount}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Queue'ya Eklenen</div>
                <div className="font-semibold text-green-700">
                  {sendReport.jobsQueued}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Atlanan</div>
                <div className="font-semibold text-orange-600">
                  {sendReport.skippedCount}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Tekrar Gönder</div>
                <div className="font-semibold text-gray-800">
                  {sendReport.resend ? "Açık" : "Kapalı"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
          <p className="text-blue-800 text-sm">
            <strong>Bilgi:</strong> Bu sayfada yayınlanmış kurs içeriklerini
            anlık olarak tekrar gönderebilirsiniz. İçerik kartlarında medya,
            doküman, link, soru, soru geri bildirimi ve tamamlama geri bildirimi
            gibi alanlar görünür. Böylece içerik oluşturmaya benzer bir seçim
            deneyimi elde edersiniz.
          </p>
        </div>

        {/* Kurs Seçimi */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3 text-sm font-bold">
              1
            </span>
            Kurs ve İçerik Seçin
          </h2>

          {loadingCourses ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
              <span className="ml-3 text-gray-500">Kurslar yükleniyor...</span>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Henüz yayınlanmış kursunuz yok.
            </div>
          ) : (
            <div className="space-y-3">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    selectedCourseId === course.id
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {/* Course Header */}
                  <div
                    className="p-4 flex items-center cursor-pointer"
                    onClick={() => handleCourseSelect(course)}
                  >
                    {/* Checkbox */}
                    <div
                      className={`w-6 h-6 rounded-md border-2 mr-3 flex items-center justify-center transition-all ${
                        selectedCourseId === course.id
                          ? "bg-green-500 border-green-500"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedCourseId === course.id && (
                        <FiCheck className="text-white" size={16} />
                      )}
                    </div>

                    {/* Course Icon */}
                    <FiBook className="text-gray-500 mr-3" size={20} />

                    {/* Course Info */}
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">
                        {course.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {course.lessons?.length || 0} içerik •{" "}
                        {course.enrollments?.length || 0} katılımcı
                      </div>
                    </div>

                    {/* Expand/Collapse */}
                    {selectedCourseId === course.id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCourseExpansion(course.id);
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700"
                      >
                        {expandedCourseId === course.id ? (
                          <FiChevronDown size={20} />
                        ) : (
                          <FiChevronRight size={20} />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Lessons List (Expanded) */}
                  {selectedCourseId === course.id &&
                    expandedCourseId === course.id &&
                    course.lessons &&
                    course.lessons.length > 0 && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm text-gray-600 mb-3">
                          {selectedLessonIds.length === 0
                            ? "Hiçbiri seçili değil = Tümü gönderilir"
                            : `${selectedLessonIds.length} içerik seçili`}
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          <button
                            type="button"
                            onClick={selectAllLessons}
                            className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100"
                          >
                            Tümünü Seç
                          </button>
                          <button
                            type="button"
                            onClick={clearLessonSelection}
                            className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200"
                          >
                            Seçimi Temizle
                          </button>
                          <button
                            type="button"
                            onClick={selectOnlyQuizLessons}
                            className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100"
                          >
                            Sadece Sorulu İçerikler
                          </button>
                          <button
                            type="button"
                            onClick={selectOnlyRichContentLessons}
                            className="px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 text-xs font-semibold hover:bg-purple-100"
                          >
                            Sadece Medya/Doküman/Link
                          </button>
                        </div>

                        <div className="space-y-2">
                          {getOrderedLessons(course).map((lesson) => {
                            const badges = getLessonBadges(lesson);

                            return (
                              <div
                                key={lesson.id}
                                className={`p-3 rounded-lg cursor-pointer transition-all ${
                                  selectedLessonIds.includes(lesson.id)
                                    ? "bg-green-100 border border-green-300"
                                    : "bg-white border border-gray-200 hover:border-gray-300"
                                }`}
                                onClick={() => toggleLessonSelection(lesson.id)}
                              >
                                <div className="flex items-start">
                                  <div
                                    className={`w-5 h-5 rounded border-2 mr-3 mt-0.5 flex items-center justify-center transition-all ${
                                      selectedLessonIds.includes(lesson.id)
                                        ? "bg-green-500 border-green-500"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    {selectedLessonIds.includes(lesson.id) && (
                                      <FiCheck
                                        className="text-white"
                                        size={12}
                                      />
                                    )}
                                  </div>

                                  <FiFileText
                                    className="text-gray-400 mr-2 mt-0.5"
                                    size={16}
                                  />

                                  <div className="flex-1">
                                    <div className="text-sm">
                                      <span className="font-medium text-gray-700">
                                        Gün {lesson.day}:
                                      </span>{" "}
                                      <span className="text-gray-600">
                                        {lesson.title}
                                      </span>
                                    </div>

                                    {badges.length > 0 && (
                                      <div className="flex flex-wrap gap-2 mt-2">
                                        {badges.map((badge) => (
                                          <span
                                            key={`${lesson.id}-${badge.label}`}
                                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${badge.className}`}
                                          >
                                            {badge.label}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                    <div className="flex flex-wrap gap-3 text-[11px] text-gray-500 mt-2">
                                      {lesson.media && (
                                        <span className="flex items-center gap-1">
                                          <FiImage size={12} /> medya
                                        </span>
                                      )}
                                      {lesson.externalLink && (
                                        <span className="flex items-center gap-1">
                                          <FiLink size={12} /> link
                                        </span>
                                      )}
                                      {lessonHasQuiz(lesson) && (
                                        <span className="flex items-center gap-1">
                                          <FiMessageSquare size={12} /> soru
                                          mevcut
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Gönderim Seçenekleri */}
        {selectedCourseId && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3 text-sm font-bold">
                2
              </span>
              Gönderim Seçenekleri
            </h2>

            <div className="space-y-4">
              {/* Resend option */}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={resendEnabled}
                  onChange={(e) => setResendEnabled(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-green-500 focus:ring-green-500 mr-3"
                />
                <div>
                  <div className="font-medium text-gray-700">Tekrar Gönder</div>
                  <div className="text-sm text-gray-500">
                    Daha önce gönderilmiş içerikleri de tekrar gönder
                  </div>
                </div>
              </label>

              {/* Info about what will be sent */}
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <div className="text-sm text-gray-600">
                  <strong>Gönderilecek:</strong> {getSendInfoText()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gönder Butonu */}
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedCourseId}
            className={`px-8 py-3 rounded-lg font-semibold text-white flex items-center transition-all ${
              isSubmitting || !selectedCourseId
                ? "bg-gray-400 cursor-not-allowed"
                : "green-button hover:opacity-90"
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Gönderiliyor...
              </>
            ) : (
              <>
                <FiSend className="mr-2" />
                İçerikleri Gönder
              </>
            )}
          </button>
        </div>
      </div>
    </SharedLayout>
  );
};

export default ForceSend;
